<template>
  <div class="vol-page" :style="rootStyle">
    <div class="top-bar">
      <a class="back-link" href="#" @click.prevent="$emit('back')">← Back to Benevolism</a>
      <div class="top-brand"><span class="mark">B</span> THE BENEVOLISM SERIES — VOL. {{ book.roman }}</div>
    </div>

    <main>
      <!-- HERO -->
      <section class="hero">
        <v-container class="hero-inner" style="max-width:1180px;">
          <div class="cover-col intro-rise">
            <div class="cover-card" :style="coverStyle">
              <div class="cover-crown">♛</div>
              <div class="cover-title" v-html="book.coverTitle"></div>
              <div class="cover-sub" v-html="book.coverSub"></div>
              <div class="cover-divider" />
              <div class="cover-scales">⚖</div>
              <div class="cover-divider" />
              <div class="cover-series">The Benevolism Series — Volume {{ book.roman }}</div>
              <div class="cover-author">ENRICO MICHAEL SEVERE</div>
              <div class="cover-heir">Davidic Heir, Son of King David</div>
            </div>
            <div class="badge">
              <div>
                <div class="badge-top">BENEVOLISM SERIES</div>
                <div class="badge-main">VOLUME</div>
                <div class="badge-bottom">{{ book.roman }}</div>
              </div>
            </div>
          </div>
          <div class="info-col intro-rise-delayed">
            <div class="eyebrow" style="text-align:left;">Volume {{ book.roman }} — The Benevolism Series</div>
            <h1 class="info-title">{{ book.title }}</h1>
            <p class="info-sub">{{ book.infoSub }}</p>
            <p class="info-desc">{{ book.infoDesc }}</p>
            <div class="stats-row">
              <div v-for="(s, i) in stats" :key="i" class="stat">
                <div class="stat-num">{{ statDisplay[i] }}</div>
                <div class="stat-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="cta-area">
              <v-btn
                class="btn-gold"
                size="large"
                rounded="0"
                elevation="0"
                :href="book.amazonReady ? book.amazonUrl : undefined"
                :target="book.amazonReady ? '_blank' : undefined"
                :disabled="!book.amazonReady"
              >
                <v-icon start>mdi-amazon</v-icon>
                {{ book.amazonReady ? 'Available on Amazon' : 'Coming Soon to Amazon' }}
              </v-btn>
              <div class="coming-note">The Benevolism Series, Volume {{ book.roman }} — {{ book.amazonReady ? 'available now.' : 'forthcoming.' }}</div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- PRINCIPLES -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Blueprint</div>
          <h2 class="heading">{{ book.blueprintHeading }}</h2>
          <p class="lede">{{ book.blueprintLede }}</p>
          <div class="layers-grid">
            <div
              v-for="(layer, i) in book.principles"
              :key="i"
              class="layer-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="layer-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <h3 class="layer-name">{{ layer.name }}</h3>
              <p class="layer-desc">{{ layer.desc }}</p>
            </div>
          </div>
        </v-container>
      </section>

      <!-- TEN LAWS -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Doctrine</div>
          <h2 class="heading">{{ book.lawsHeading }}</h2>
          <p class="lede">
            The principles of Benevolism, applied to {{ book.domain }}, yield binding standards — the line between a system that is genuinely benevolent and one that merely claims to be.
          </p>
          <div class="laws-grid">
            <div
              v-for="(law, i) in book.laws"
              :key="i"
              class="law-card reveal"
              :style="{ transitionDelay: (i * 40) + 'ms' }"
            >
              <div class="law-numeral">{{ law.numeral }}</div>
              <div>
                <div class="law-name">{{ law.name }}</div>
                <p class="law-desc">{{ law.desc }}</p>
              </div>
            </div>
          </div>
          <div class="pull-quote">
            <div class="mark">"</div>
            <blockquote>{{ book.lawsQuote }}</blockquote>
          </div>
        </v-container>
      </section>

      <!-- PLEDGE -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Pledge</div>
          <h2 class="heading">{{ book.pledgeHeading }}</h2>
          <p class="lede">{{ book.pledgeLede }}</p>
          <div class="pledge-tabs">
            <button
              v-for="(pledge, i) in book.pledges"
              :key="i"
              class="pledge-tab"
              :class="{ active: activePledge === i }"
              @click="activePledge = i"
            >
              {{ pledge.role }}
            </button>
          </div>
          <div class="pledge-panel">
            <transition name="fade-swap" mode="out-in">
              <p :key="activePledge">{{ book.pledges[activePledge].text }}</p>
            </transition>
          </div>
        </v-container>
      </section>

      <!-- CLOSING -->
      <section class="closing">
        <v-container class="closing-inner reveal" style="max-width:780px;">
          <div class="mark">"</div>
          <blockquote>{{ book.closingQuote }}</blockquote>
          <div class="attr">— Enrico Michael Severe</div>
          <div class="closing-buttons">
            <v-btn class="btn-gold" size="large" rounded="0" elevation="0" @click="scrollToTop">
              Back to the Top
            </v-btn>
            <v-btn class="btn-ghost" size="large" rounded="0" elevation="0" @click="$emit('back')">
              ← Back to Benevolism
            </v-btn>
          </div>
        </v-container>
      </section>
    </main>

    <footer>
      <div class="footer-text">
        © {{ new Date().getFullYear() }} Enrico Michael Severe. Part of
        <a href="#" @click.prevent="$emit('back')">The Benevolism Series</a>. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'BenevolentVolume',
  props: {
    book: { type: Object, required: true },
  },
  emits: ['back'],
  data: () => ({
    activePledge: 0,
    statDisplay: [0, 0, 0, ''],
  }),
  computed: {
    rootStyle () {
      return {
        '--vol-bg': this.book.colors.bg,
        '--vol-mid': this.book.colors.mid,
        '--vol-light': this.book.colors.light,
      }
    },
    coverStyle () {
      return {
        background: `linear-gradient(160deg, ${this.book.colors.grad1} 0%, ${this.book.colors.bg} 60%, ${this.book.colors.grad3} 100%)`,
      }
    },
    stats () {
      return [
        { label: 'Principles', value: this.book.principles.length },
        { label: 'Laws', value: this.book.laws.length },
        { label: 'Pledges', value: this.book.pledges.length },
        { label: 'Series Volume', value: this.book.roman },
      ]
    },
  },
  mounted () {
    window.scrollTo({ top: 0 })
    this.animateStats()
    this.$nextTick(() => this.setupReveal())
  },
  beforeUnmount () {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    scrollToTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    animateStats () {
      const duration = 900
      this.stats.forEach((s, i) => {
        if (typeof s.value !== 'number') {
          this.$set(this.statDisplay, i, s.value)
          return
        }
        const target = s.value
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration)
          this.$set(this.statDisplay, i, Math.round(target * progress))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    },
    setupReveal () {
      const els = this.$el.querySelectorAll('.reveal, .reveal-line')
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
      els.forEach((el) => observer.observe(el))
      this._revealObserver = observer
    },
  },
}
</script>

<style>
.vol-page {
  background: var(--vol-bg);
  color: var(--ivory);
  font-family: var(--font-body);
}

/* ===== ENTRANCE + SCROLL REVEAL ===== */
@keyframes introRise {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
}
.vol-page .intro-rise { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) both; }
.vol-page .intro-rise-delayed { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) 0.12s both; }

.vol-page .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.vol-page .reveal.is-visible { opacity: 1; transform: translateY(0); }

.vol-page .reveal-line {
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s ease;
}
.vol-page .reveal-line.is-visible { transform: scaleX(1); }

.vol-page .top-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--vol-mid);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(201,168,76,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
}
.vol-page .back-link {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--gold-dim);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.vol-page .back-link:hover { color: var(--gold); }
.vol-page .top-brand {
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.18em;
  color: var(--ivory-dim);
  display: flex;
  align-items: center;
  gap: 8px;
}
.vol-page .top-brand .mark {
  width: 24px; height: 24px;
  background: var(--gold);
  color: var(--vol-bg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
}

.vol-page .eyebrow {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: var(--gold);
  text-transform: uppercase;
  margin-bottom: 16px;
}
.vol-page .heading {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: var(--ivory);
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;
}
.vol-page .lede {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.75;
  color: var(--ivory-dim);
  font-weight: 300;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}
.vol-page section { padding: 96px 0; }
.vol-page section.alt { background: var(--vol-mid); border-top: 1px solid rgba(201,168,76,0.1); border-bottom: 1px solid rgba(201,168,76,0.1); }

.vol-page .hero { padding: 72px 0 96px; position: relative; overflow: hidden; }
.vol-page .hero::before {
  content: '';
  position: absolute;
  top: -200px; right: -200px;
  width: 700px; height: 700px;
  background: radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.vol-page .hero-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 80px;
  align-items: start;
  position: relative;
  z-index: 1;
}
@media (max-width: 900px) {
  .vol-page .hero-inner { grid-template-columns: 1fr; }
  .vol-page .cover-col { display: flex; flex-direction: column; align-items: center; }
}
.vol-page .cover-col { position: relative; }
.vol-page .cover-card {
  width: 260px;
  min-height: 360px;
  background: linear-gradient(160deg, var(--vol-light) 0%, var(--vol-bg) 60%, var(--vol-mid) 100%);
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 0 0 0 1px rgba(201,168,76,0.1), 8px 8px 32px rgba(0,0,0,0.6), inset 0 0 60px rgba(201,168,76,0.03);
  padding: 32px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 9px;
  transform: rotate(-2deg);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
}
.vol-page .cover-card::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(201,168,76,0.2);
  pointer-events: none;
}
.vol-page .cover-card::after {
  content: '';
  position: absolute;
  top: 0; left: -150%;
  width: 60%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(232,204,128,0.18), transparent);
  transform: skewX(-20deg);
  transition: left 0.7s ease;
  pointer-events: none;
}
.vol-page .cover-card:hover::after { left: 150%; }
.vol-page .cover-card:hover { transform: rotate(0deg) scale(1.02); }
.vol-page .cover-crown { font-size: 34px; margin-bottom: 2px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.vol-page .cover-title {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 400;
  color: var(--gold-light);
  letter-spacing: 0.08em;
  line-height: 1.4;
}
.vol-page .cover-title strong { font-size: 21px; font-weight: 900; display: block; letter-spacing: 0.02em; }
.vol-page .cover-divider {
  width: 80%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  margin: 4px 0;
}
.vol-page .cover-sub { font-family: var(--font-body); font-size: 11px; color: var(--ivory-dim); line-height: 1.5; font-style: italic; }
.vol-page .cover-scales { font-size: 30px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); margin: 4px 0; }
.vol-page .cover-series { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.15em; color: var(--ivory-dim); }
.vol-page .cover-author { font-family: var(--font-display); font-size: 12px; font-weight: 700; color: var(--gold-light); letter-spacing: 0.12em; }
.vol-page .cover-heir { font-family: var(--font-body); font-size: 9px; font-style: italic; color: var(--ivory-dim); }

.vol-page .badge {
  position: absolute;
  top: -12px; right: -12px;
  width: 76px; height: 76px;
  background: var(--gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--vol-bg);
  animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) 0.5s both;
}
@keyframes badgePop {
  from { opacity: 0; transform: rotate(15deg) scale(0.4); }
  to { opacity: 1; transform: rotate(15deg) scale(1); }
}
.vol-page .badge-top { font-family: var(--font-ui); font-size: 7px; font-weight: 700; letter-spacing: 0.08em; }
.vol-page .badge-main { font-family: var(--font-display); font-size: 11px; font-weight: 900; line-height: 1.1; }
.vol-page .badge-bottom { font-family: var(--font-ui); font-size: 8px; font-weight: 600; }

.vol-page .info-title {
  font-family: var(--font-display);
  font-size: clamp(30px, 3.4vw, 46px);
  font-weight: 700;
  color: var(--ivory);
  margin-bottom: 8px;
  line-height: 1.15;
}
.vol-page .info-sub {
  font-family: var(--font-display);
  font-size: 17px;
  font-style: italic;
  color: var(--gold-dim);
  margin-bottom: 22px;
  line-height: 1.5;
}
.vol-page .info-desc {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.75;
  color: var(--ivory-dim);
  margin-bottom: 32px;
  font-weight: 300;
  max-width: 620px;
}
.vol-page .stats-row {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 36px;
  border: 1px solid rgba(201,168,76,0.15);
  width: fit-content;
}
.vol-page .stat { padding: 16px 26px; text-align: center; position: relative; }
.vol-page .stat:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 60px;
  background: rgba(201,168,76,0.15);
}
.vol-page .stat-num { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); line-height: 1; }
.vol-page .stat-label { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.13em; color: var(--slate-light); text-transform: uppercase; margin-top: 4px; }

.vol-page .cta-area { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.vol-page .btn-gold { background: var(--gold) !important; color: var(--vol-bg) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 700 !important; letter-spacing: 0.08em !important; padding: 0 32px !important; height: 52px !important; }
.vol-page .btn-gold:hover { background: var(--gold-light) !important; }
.vol-page .coming-note { font-family: var(--font-ui); font-size: 11px; color: var(--slate-light); letter-spacing: 0.02em; }

.vol-page .layers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2px; margin-bottom: 56px; }
.vol-page .layer-card { background: var(--vol-bg); border: 1px solid rgba(201,168,76,0.08); padding: 32px 28px; transition: border-color 0.2s, background 0.2s; }
.vol-page .layer-card:hover { border-color: rgba(201,168,76,0.25); background: var(--vol-light); }
.vol-page .layer-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; margin-bottom: 12px; }
.vol-page .layer-name { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.vol-page .layer-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

/* fade-swap transition, used for pledge text and pricing stage swaps */
/* Vue 3 naming */
.vol-page .fade-swap-enter-active,
.vol-page .fade-swap-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.vol-page .fade-swap-enter-from { opacity: 0; transform: translateY(6px); }
.vol-page .fade-swap-leave-to { opacity: 0; transform: translateY(-6px); }
/* Vue 2 naming (same active/to classes, "from" state is unsuffixed) */
.vol-page .fade-swap-enter { opacity: 0; transform: translateY(6px); }
.vol-page .fade-swap-leave { opacity: 0; transform: translateY(-6px); }

.vol-page .laws-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-bottom: 48px; }
@media (max-width: 768px) { .vol-page .laws-grid { grid-template-columns: 1fr; } }
.vol-page .law-card { display: flex; gap: 22px; padding: 32px 36px; background: var(--vol-bg); border: 1px solid rgba(201,168,76,0.06); transition: border-color 0.2s, background 0.2s; }
.vol-page .law-card:hover { border-color: rgba(201,168,76,0.2); background: var(--vol-light); }
.vol-page .law-numeral { font-family: var(--font-display); font-size: 13px; font-style: italic; color: var(--gold-dim); flex-shrink: 0; padding-top: 2px; width: 22px; }
.vol-page .law-name { font-family: var(--font-display); font-size: 16px; font-weight: 600; color: var(--ivory); margin-bottom: 9px; }
.vol-page .law-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.vol-page .pull-quote { max-width: 640px; margin: 0 auto; text-align: center; }
.vol-page .pull-quote .mark { font-family: var(--font-display); font-size: 48px; color: var(--gold); line-height: 1; }
.vol-page .pull-quote blockquote { font-family: var(--font-display); font-style: italic; font-size: 19px; line-height: 1.55; color: var(--ivory); margin: 4px 0 0; }

.vol-page .pledge-tabs { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 40px; }
.vol-page .pledge-tab { background: transparent; border: 1px solid rgba(201,168,76,0.2); color: var(--ivory-dim); font-family: var(--font-ui); font-size: 12px; font-weight: 600; letter-spacing: 0.05em; padding: 11px 22px; cursor: pointer; transition: all 0.2s; }
.vol-page .pledge-tab:hover { border-color: var(--gold); color: var(--gold); }
.vol-page .pledge-tab.active { background: var(--gold); border-color: var(--gold); color: var(--vol-bg); }
.vol-page .pledge-panel { max-width: 760px; margin: 0 auto; background: var(--vol-mid); border: 1px solid rgba(201,168,76,0.12); padding: 44px 48px; }
.vol-page .pledge-panel p { font-family: var(--font-body); font-size: 15px; line-height: 1.8; color: var(--ivory-dim); font-weight: 300; }

.vol-page .actions-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2px; }
.vol-page .action-card { display: flex; gap: 20px; padding: 34px; background: var(--vol-mid); border: 1px solid rgba(201,168,76,0.08); transition: border-color 0.2s, background 0.2s; }
.vol-page .action-card:hover { border-color: rgba(201,168,76,0.25); background: var(--vol-light); }
.vol-page .action-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; padding-top: 4px; width: 26px; flex-shrink: 0; }
.vol-page .action-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.vol-page .action-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.vol-page .closing { text-align: center; padding: 110px 0 96px; position: relative; overflow: hidden; }
.vol-page .closing::before {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.vol-page .closing-inner { position: relative; z-index: 1; }
.vol-page .closing .mark { font-family: var(--font-display); font-size: 56px; color: var(--gold); line-height: 1; }
.vol-page .closing blockquote { font-family: var(--font-display); font-style: italic; font-size: clamp(22px, 3vw, 30px); line-height: 1.5; color: var(--ivory); margin: 8px 0 20px; }
.vol-page .closing .attr { font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.15em; color: var(--gold-dim); text-transform: uppercase; margin-bottom: 48px; }
.vol-page .closing-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
.vol-page .btn-ghost { background: transparent !important; border: 1px solid rgba(245,240,232,0.25) !important; color: var(--ivory-dim) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 500 !important; letter-spacing: 0.06em !important; padding: 0 32px !important; height: 52px !important; }
.vol-page .btn-ghost:hover { border-color: var(--ivory-dim) !important; color: var(--ivory) !important; }

.vol-page footer { padding: 40px 0; background: var(--vol-mid); border-top: 1px solid rgba(201,168,76,0.12); text-align: center; }
.vol-page .footer-text { font-family: var(--font-ui); font-size: 11px; color: rgba(200,192,176,0.5); letter-spacing: 0.05em; }
.vol-page .footer-text a { color: var(--gold-dim); text-decoration: none; cursor: pointer; }
.vol-page .footer-text a:hover { color: var(--gold); }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .vol-page * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .vol-page .reveal,
  .vol-page .reveal-line {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>